import React, { useMemo } from "react";
import { isPresent, isLate, isAbsent } from "./StaffAnalyticsTab";
import TabKpiRow from "./TabKpiRow";
import AnalyticsNetworkTrend from "./AnalyticsNetworkTrend";

const isExpected = (att) =>
  att?.status_value !== "on_leave" && att?.status_value !== "day_off";

const computeGymScore = (shifts) => {
  const map = {};
  shifts.forEach(({ gymId, gymName, att }) => {
    if (!map[gymId]) map[gymId] = { gymId, gymName, exp: 0, pres: 0, onTime: 0 };
    const g = map[gymId];
    if (!isExpected(att)) return;
    g.exp++;
    if (isPresent(att)) { g.pres++; if (!isLate(att)) g.onTime++; }
  });
  return Object.values(map).map((g) => {
    const att   = g.exp  > 0 ? (g.pres   / g.exp)  * 100 : 0;
    const punct = g.pres > 0 ? (g.onTime / g.pres) * 100 : 100;
    const score = Math.round(att * 0.6 + punct * 0.4);
    return { ...g, score };
  });
};

const computeOverviewKpis = (allShifts, prevShifts) => {
  const exp  = allShifts.filter(({ att }) => isExpected(att));
  const pres = exp.filter(({ att }) => isPresent(att));
  const late = exp.filter(({ att }) => isLate(att));
  const abs  = exp.filter(({ att }) => isAbsent(att));
  const attRate  = exp.length > 0 ? Math.round((pres.length / exp.length) * 100) : 0;
  const lateRate = exp.length > 0 ? Math.round((late.length / exp.length) * 100) : 0;
  const absRate  = exp.length > 0 ? Math.round((abs.length  / exp.length) * 100) : 0;
  const critical = computeGymScore(allShifts).filter((g) => g.score < 60).length;

  const prevExp  = prevShifts.filter(({ att }) => isExpected(att));
  const prevPres = prevExp.filter(({ att }) => isPresent(att));
  const prevLate = prevExp.filter(({ att }) => isLate(att));
  const prevAbs  = prevExp.filter(({ att }) => isAbsent(att));
  const prevAtt  = prevExp.length > 0 ? Math.round((prevPres.length / prevExp.length) * 100) : null;
  const prevLr   = prevExp.length > 0 ? Math.round((prevLate.length / prevExp.length) * 100) : null;
  const prevAr   = prevExp.length > 0 ? Math.round((prevAbs.length  / prevExp.length) * 100) : null;
  const prevCrit = computeGymScore(prevShifts).filter((g) => g.score < 60).length;

  const mkDelta = (curr, prev, invert = false) => {
    if (prev == null) return undefined;
    const d = curr - prev;
    return { text: `${d >= 0 ? "+" : ""}${d} vs prev`, up: invert ? d <= 0 : d >= 0 };
  };

  return [
    { label: "Attendance %", value: `${attRate}%`,  mod: attRate >= 80 ? "green" : attRate >= 60 ? "orange" : "red",  delta: mkDelta(attRate, prevAtt) },
    { label: "Late %",       value: `${lateRate}%`, mod: lateRate < 10 ? "green" : lateRate <= 20 ? "orange" : "red", delta: mkDelta(lateRate, prevLr, true) },
    { label: "Absence %",    value: `${absRate}%`,  mod: absRate < 5 ? "green" : absRate <= 15 ? "orange" : "red",    delta: mkDelta(absRate, prevAr, true) },
    { label: "Critical Gyms",value: critical,        mod: critical === 0 ? "green" : critical <= 2 ? "orange" : "red", delta: mkDelta(critical, prevCrit > 0 ? prevCrit : null, true) },
  ];
};

const buildOffenders = (allShifts, prevShifts) => {
  const map = {};
  allShifts.forEach(({ userId, name, att }) => {
    if (!map[userId]) map[userId] = { userId, name, lateCount: 0 };
    if (isLate(att)) map[userId].lateCount++;
  });
  const prevIds = new Set(prevShifts.map((s) => s.userId));
  return Object.values(map)
    .filter((s) => s.lateCount > 0)
    .sort((a, b) => b.lateCount - a.lateCount)
    .slice(0, 5)
    .map((s) => ({ ...s, isRepeat: prevIds.has(s.userId) }));
};

const scoreClass = (s) =>
  s >= 80 ? "sta-score-pill--green" : s >= 60 ? "sta-score-pill--yellow" : "sta-score-pill--red";

const AnalyticsOverviewTab = ({ allShifts = [], prevShifts = [], dateFrom, dateTo }) => {
  const kpiCards  = useMemo(() => computeOverviewKpis(allShifts, prevShifts), [allShifts, prevShifts]);
  const prevMap   = useMemo(() => {
    const m = {};
    computeGymScore(prevShifts).forEach((g) => { m[g.gymId] = g.score; });
    return m;
  }, [prevShifts]);
  const worst5    = useMemo(() => computeGymScore(allShifts).sort((a, b) => a.score - b.score).slice(0, 5), [allShifts]);
  const offenders = useMemo(() => buildOffenders(allShifts, prevShifts), [allShifts, prevShifts]);

  return (
    <div>
      <TabKpiRow cards={kpiCards} />

      <div className="sta-overview-v2__trend">
        <div className="sta-panel-v2__title">Network Attendance Trend</div>
        <AnalyticsNetworkTrend allShifts={allShifts} dateFrom={dateFrom} dateTo={dateTo} />
      </div>

      <div className="sta-panels-row">
        <div className="sta-panel-v2">
          <div className="sta-panel-v2__title">Worst 5 Gyms</div>
          {worst5.length === 0 ? (
            <div className="sta-empty" style={{ padding: 12 }}>No gym data.</div>
          ) : (
            worst5.map((g) => {
              const delta = prevMap[g.gymId] != null ? g.score - prevMap[g.gymId] : null;
              return (
                <div key={g.gymId} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid #e9ecef", fontSize:12 }}>
                  <span style={{ fontWeight:600 }}>{g.gymName}</span>
                  <span style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span className={`sta-score-pill ${scoreClass(g.score)}`}>{g.score}</span>
                    {delta != null && (
                      <span className={delta >= 0 ? "sta-delta--up" : "sta-delta--down"} style={{ fontSize:11 }}>
                        {delta >= 0 ? `↑ +${delta}` : `↓ ${delta}`}
                      </span>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="sta-panel-v2">
          <div className="sta-panel-v2__title">Discipline Alerts — Top Late Staff</div>
          {offenders.length === 0 ? (
            <div className="sta-empty" style={{ padding: 12 }}>No late staff this period!</div>
          ) : (
            <ul className="sta-alerts-list">
              {offenders.map((s) => (
                <li key={s.userId} className="sta-alerts-list__item">
                  <span className="sta-alerts-list__name">
                    {s.name}
                    {s.isRepeat && <span className="sta-repeat-badge" style={{ marginLeft:4 }}>REPEAT</span>}
                  </span>
                  <span style={{ color:"#fd7e14", fontWeight:600, fontSize:12 }}>{s.lateCount} late</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverviewTab;
